import { type SalesTask, type SalesTaskActivityType } from '@erp/shared';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useSession } from '@/features/auth';
import { useCreateSalesTask } from '../api/use-sales-assistant';
function localInput(value: Date): string {
  return new Date(value.getTime() - value.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}
export function CustomerCareTaskDialog({
  customerId,
  customerName,
  activityType,
  onOpenChange,
  onCreated,
  initialReason,
  initialTitle,
}: {
  customerId: string;
  customerName: string;
  activityType: SalesTaskActivityType | null;
  onOpenChange: (open: boolean) => void;
  onCreated?: (task: SalesTask) => void;
  initialReason?: string;
  initialTitle?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const session = useSession();
  const create = useCreateSalesTask();
  const [dueAt, setDueAt] = useState(localInput(new Date(Date.now() + 86_400_000)));
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (activityType === null) return;
    setReason(initialReason ?? t(`salesAssistant.care.taskReasons.${activityType}`));
    setDueAt(localInput(new Date(Date.now() + 86_400_000)));
  }, [activityType, initialReason, t]);
  return (
    <Dialog open={activityType !== null} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('salesAssistant.care.taskDialogTitle')}</DialogTitle>
          <DialogDescription>{customerName}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="customer-care-due">
              {t('salesAssistant.cockpit.taskDialog.dueAt')}
            </Label>
            <Input
              id="customer-care-due"
              type="datetime-local"
              value={dueAt}
              onChange={(event) => setDueAt(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="customer-care-reason">
              {t('salesAssistant.cockpit.taskDialog.reason')}
            </Label>
            <Textarea
              id="customer-care-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            disabled={
              activityType === null ||
              session.data === null ||
              session.data === undefined ||
              reason.trim() === '' ||
              !Number.isFinite(new Date(dueAt).getTime()) ||
              create.isPending
            }
            onClick={() => {
              if (activityType === null || session.data === null || session.data === undefined)
                return;
              create.mutate(
                {
                  customerId,
                  recommendationId: null,
                  title: initialTitle ?? t(`salesAssistant.care.taskTitles.${activityType}`),
                  reason: reason.trim(),
                  assignedUserId: session.data.id,
                  assignedTeamId: null,
                  priority: 'MEDIUM',
                  dueAt: new Date(dueAt).toISOString(),
                  activityType,
                  checklist: [],
                  attachmentIds: [],
                },
                {
                  onSuccess: (task) => {
                    onCreated?.(task);
                    toast.success(t('salesAssistant.cockpit.taskDialog.success'));
                    onOpenChange(false);
                  },
                  onError: (error) => toast.error(error.message),
                },
              );
            }}
          >
            {create.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : null}
            {t('salesAssistant.cockpit.taskDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
